import React, { useCallback, useEffect, useState } from 'react';
import { FileDown, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate, formatLitres, formatNombre } from '../../lib/formats';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';
import { EmptyState } from '../ui/empty-state';

/**
 * Déclaration hebdomadaire des stocks (lot 034) : litres reçus et vendus par
 * produit et par station, du lundi au dimanche. La date choisie est ramenée à
 * son lundi par le serveur ; PDF et Excel se téléchargent d'ici.
 */

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground';

/** Lundi de la semaine dernière, en date locale AAAA-MM-JJ. */
const lundiDerniereSemaine = () => {
  const date = new Date();
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7) - 7);
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 10);
};

const WeeklyDeclaration = () => {
  const [jour, setJour] = useState(lundiDerniereSemaine());
  const [declaration, setDeclaration] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [telechargement, setTelechargement] = useState('');

  const charger = useCallback(async (date) => {
    setChargement(true);
    try {
      setDeclaration(await stationNetworkApi.declaration.get(date));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger la déclaration'));
      setDeclaration(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (jour) charger(jour);
  }, [jour, charger]);

  const telecharger = async (format) => {
    setTelechargement(format);
    try {
      await stationNetworkApi.declaration.download(jour, format);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de télécharger le fichier'));
    } finally {
      setTelechargement('');
    }
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <label htmlFor="declaration-jour" className="text-sm font-medium text-foreground">
            Semaine du
          </label>
          <Input id="declaration-jour" type="date" value={jour} onChange={(event) => event.target.value && setJour(event.target.value)} className="h-9 w-44" />
        </div>
        {declaration && (
          <p className="pb-2 text-sm text-muted-foreground tabular-nums">
            Du lundi {formatDate(declaration.week_start)} au dimanche {formatDate(declaration.week_end)}
          </p>
        )}
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" disabled={!declaration || telechargement === 'pdf'} onClick={() => telecharger('pdf')}>
            <FileDown className="h-4 w-4" aria-hidden="true" />
            PDF
          </Button>
          <Button type="button" variant="outline" disabled={!declaration || telechargement === 'xlsx'} onClick={() => telecharger('xlsx')}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            Excel
          </Button>
        </div>
      </div>

      {chargement && !declaration ? (
        <LoadingState shape="table" />
      ) : !declaration || declaration.rows.length === 0 ? (
        <EmptyState variant="empty" title="Aucun mouvement cette semaine" className="border-0" />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <section className="overflow-x-auto rounded-xl border border-border bg-card px-1 py-2 shadow-sm">
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Station</th>
                  <th className="text-left">Ville</th>
                  <th className="text-left">Produit</th>
                  <th className="text-right">Reçu</th>
                  <th className="text-right">Vendu</th>
                </tr>
              </thead>
              <tbody>
                {declaration.rows.map((ligne) => (
                  <tr key={`${ligne.station_id}-${ligne.product_id}`}>
                    <td className="font-medium">{ligne.station_name}</td>
                    <td>{ligne.city || '—'}</td>
                    <td>{ligne.product_name || 'Produit à préciser'}</td>
                    <td className="text-right tabular-nums">{formatLitres(ligne.received_liters, 2)}</td>
                    <td className="text-right tabular-nums">{formatLitres(ligne.sold_liters, 2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan={3}>Total</td>
                  <td className="text-right tabular-nums">{formatLitres(declaration.totals.received_liters, 2)}</td>
                  <td className="text-right tabular-nums">{formatLitres(declaration.totals.sold_liters, 2)}</td>
                </tr>
              </tfoot>
            </table>
          </section>

          <section className="self-start overflow-x-auto rounded-xl border border-border bg-card px-1 py-2 shadow-sm">
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Produit</th>
                  <th className="text-right">Reçu</th>
                  <th className="text-right">Vendu</th>
                </tr>
              </thead>
              <tbody>
                {declaration.totals_by_product.map((ligne) => (
                  <tr key={ligne.product_id || 'sans-produit'}>
                    <td className="font-medium">{ligne.product_name || 'Produit à préciser'}</td>
                    <td className="text-right tabular-nums">{formatNombre(ligne.received_liters, 2)} L</td>
                    <td className="text-right tabular-nums">{formatNombre(ligne.sold_liters, 2)} L</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  );
};

export default WeeklyDeclaration;
